/**
 * Evaluates automation rule condition trees against a data record.
 *
 * Supports 10 condition types including nested AND/OR combinators.
 * Missing fields produce warnings and fail the condition (skip rule, no crash).
 */

import type { AutomationCondition, FieldMapping } from '@/types/pm-types';

export interface ConditionEvaluationResult {
  passed: boolean;
  warnings: string[];
}

export function evaluateConditions(
  conditions: AutomationCondition[],
  record: Record<string, unknown>,
  _fieldMapping?: FieldMapping,
): ConditionEvaluationResult {
  const warnings: string[] = [];

  if (!conditions || conditions.length === 0) {
    return { passed: true, warnings };
  }

  // Top-level conditions are evaluated as implicit AND
  const passed = conditions.every((condition) =>
    evaluateSingleCondition(condition, record, warnings),
  );

  return { passed, warnings };
}

function evaluateSingleCondition(
  condition: AutomationCondition,
  record: Record<string, unknown>,
  warnings: string[],
): boolean {
  switch (condition.type) {
    case 'and':
      return evaluateCombinator(condition, record, warnings, 'and');
    case 'or':
      return evaluateCombinator(condition, record, warnings, 'or');
    default:
      return evaluateFieldCondition(condition, record, warnings);
  }
}

function evaluateCombinator(
  condition: AutomationCondition,
  record: Record<string, unknown>,
  warnings: string[],
  mode: 'and' | 'or',
): boolean {
  const subs = condition.conditions;
  if (!subs || subs.length === 0) {
    return true; // Empty combinator passes
  }

  if (mode === 'and') {
    return subs.every((sub) => evaluateSingleCondition(sub, record, warnings));
  }
  return subs.some((sub) => evaluateSingleCondition(sub, record, warnings));
}

function evaluateFieldCondition(
  condition: AutomationCondition,
  record: Record<string, unknown>,
  warnings: string[],
): boolean {
  const { type, field, value, values } = condition;

  // fieldEmpty and fieldNotEmpty don't require the field to exist in the record
  if (type === 'fieldEmpty') {
    if (!field) {
      warnings.push(`Condition "${type}" missing field property`);
      return false;
    }
    const v = record[field];
    return v == null || v === '';
  }

  if (type === 'fieldNotEmpty') {
    if (!field) {
      warnings.push(`Condition "${type}" missing field property`);
      return false;
    }
    const v = record[field];
    return v != null && v !== '';
  }

  // All other field conditions require the field to exist
  if (!field) {
    warnings.push(`Condition "${type}" missing field property`);
    return false;
  }

  if (!(field in record)) {
    warnings.push(`Field "${field}" not found in record — skipping condition`);
    return false;
  }

  const fieldValue = record[field];

  switch (type) {
    case 'fieldEquals':
      // Intentional loose equality (==) to allow cross-type comparison:
      // e.g. numeric string "42" matches number 42, which is common in data records
      return fieldValue == value;

    case 'fieldNotEquals':
      // Intentional loose inequality (!=) — mirrors fieldEquals semantics
      return fieldValue != value;

    case 'fieldContains':
      return String(fieldValue ?? '')
        .toLowerCase()
        .includes(String(value ?? '').toLowerCase());

    case 'fieldGreaterThan':
      return Number(fieldValue) > Number(value);

    case 'fieldLessThan':
      return Number(fieldValue) < Number(value);

    case 'fieldInList': {
      const list = values ?? (Array.isArray(value) ? value : []);
      return list.includes(fieldValue);
    }

    default:
      warnings.push(`Unknown condition type: "${type}"`);
      return false;
  }
}
